import { useTranslate } from '@refinedev/core';
import { Card, Flex, Grid, Tabs, Typography, theme } from 'antd';
import { StatCell } from '@/components/common/show/StatCell';
import type {
  OrganizationOut,
  PublisherListOut,
} from '@/api/generated/schemas';
import { ShowIconLink } from '@/components/common/ShowIconLink';
import { formatDate } from '@/utils/format';
import { CONTRACT_SHOW_TABS } from '../constants';
import type { ContractOut, ContractShowTab } from '../constants';

const { Text, Title } = Typography;

const TAB_LABEL_KEYS: Record<ContractShowTab, string> = {
  general: 'base.sections.basic_info',
};

interface ContractShowHeaderProps {
  record: ContractOut;
  organization?: OrganizationOut;
  publisher?: PublisherListOut;
  activeTab: ContractShowTab;
  onTabChange: (key: string) => void;
}

export const ContractShowHeader = ({
  record,
  organization,
  publisher,
  activeTab,
  onTabChange,
}: ContractShowHeaderProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();

  return (
    <Card styles={{ body: { paddingBottom: 0 } }}>
      <Flex gap={token.marginLG} align="flex-start" vertical={!screens.sm}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Flex gap={token.marginSM} align="baseline" wrap>
            {/* Long names wrap instead of pushing the tabs bar out. */}
            <Title
              level={3}
              style={{
                margin: 0,
                fontSize: token.fontSizeHeading3,
                overflowWrap: 'anywhere',
              }}
            >
              <ShowIconLink
                resource="tenancy.organizations"
                id={record.organization_id}
              >
                {organization?.name ?? record.organization_id}
              </ShowIconLink>
            </Title>
          </Flex>

          <Text
            type="secondary"
            style={{ display: 'block', marginTop: token.marginXXS }}
          >
            {`ID ${record.id}`}
          </Text>

          <Flex
            gap={token.marginXL + token.margin}
            style={{ marginTop: token.marginLG }}
            wrap
          >
            <StatCell label={t('tenancy.contracts.fields.publisher')}>
              {record.publisher_id != null ? (
                <ShowIconLink
                  resource="user_catalog.publishers"
                  id={record.publisher_id}
                >
                  {publisher?.ancestry_with_name ?? record.publisher_id}
                </ShowIconLink>
              ) : (
                t('tenancy.contracts.publisher_default')
              )}
            </StatCell>
            <StatCell label={t('tenancy.contracts.fields.valid_from')}>
              {formatDate(record.valid_from)}
            </StatCell>
            <StatCell label={t('tenancy.contracts.fields.rates')}>
              {record.rates.length}
            </StatCell>
          </Flex>
        </div>
      </Flex>

      <Tabs
        activeKey={activeTab}
        onChange={onTabChange}
        style={{ marginTop: token.marginSM }}
        tabBarStyle={{ marginBottom: 0 }}
        items={CONTRACT_SHOW_TABS.map((key) => ({
          key,
          label: t(TAB_LABEL_KEYS[key]),
        }))}
      />
    </Card>
  );
};
